import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { reportUnreadableSavedState } from '@/shared/lib/unreadableSavedState';
import {
  type AnchorPointId,
  DEFAULT_ANCHOR_POINT,
  isAnchorPointId,
  nextAnchorPoint,
  prevAnchorPoint,
} from '@/features/scene/lib/anchorPoints';

export type Projection = 'perspective' | 'iso' | 'top' | 'front' | 'side';
type SelectionMode = 'part' | 'module';
export type RenderMode = 'solid' | 'wireframe' | 'xray' | 'flat';
export type LightPreset = 'studio' | 'showroom' | 'daylight' | 'photo';
export type HdriPreset = 'neutral' | 'warm' | 'cool';
/** Source identity of the bundled deterministic 360-degree studio maps. */
export const HDRI_PROVENANCE: Record<HdriPreset, string> = {
  neutral: 'holzcom.procedural-equirectangular.studio-neutral.v1',
  warm: 'holzcom.procedural-equirectangular.studio-warm.v1',
  cool: 'holzcom.procedural-equirectangular.studio-cool.v1',
};
export type SourceScenePalette = Readonly<{
  facadeDecor: 'halifax' | 'casella_light';
  carcassColorHex: string;
  backColorHex: string;
}>;
/** DIM-волна: 'inline' — режим «Внутри» (БАЗИС-Шкаф-стиль): размеры внутри
 * ниш ВЫБРАННОГО модуля (DOM-оверлей), а не выноски вокруг корпуса. */
export type DimensionScope = 'off' | 'overall' | 'modules' | 'sections' | 'selection' | 'both' | 'all' | 'inline';
export type DimensionStyle = 'client' | 'production' | 'install';
/** DIM-волна: ортокамера «Чертёж» — плоские виды, в которых каскад размеров
 * всегда полный. null — режим снят (перспектива/изометрия, walkthrough). */
export type DraftingView = 'front' | 'side' | 'top';
type EffectsCfg = { ssao: boolean; bloom: boolean; photoreal: boolean; photoDesaturation: number; photoDepthOfField: number; selectiveEmissiveBloom: boolean;
  /** VIS-25: режим «как в каталоге» — без ACES-тонмаппинга, цвета свотчей прайса читаются буквально. */
  catalogColors: boolean };
export type SectionAxis = 'x' | 'y' | 'z' | null;
export type ViewerLayerMode = 'body' | 'details' | 'tech';
export type ModuleEditMode = 'off' | 'translate' | 'scale' | 'rotate';
export type ModuleUxProfile = 'client' | 'technologist' | 'install';
export type ConflictReason = 'collision' | 'floor' | 'bounds' | 'scale' | 'frozen-axis';
export type ModuleTransform = {
  position: [number, number, number];
  scale: [number, number, number];
};
type PanelTransform = {
  position: [number, number, number];
  rotationDeg: [number, number, number];
};
/** Copy/paste buffer for module instances.  `sourceModelId` records where the
 *  ids were copied from so paste can decide between an in-model duplicate and a
 *  cross-model add (see usePasteModules). */
type ModuleClipboard = {
  sourceModelId: string;
  moduleIds: string[];
};
export type ModuleEditModeCapability = { enabled: boolean; reason?: string };
export const MODULE_EDIT_NO_ACTIVE_REASON = 'Сначала выберите модуль';
export const MODULE_EDIT_LOCKED_REASON = 'Положение модуля зафиксировано: сначала разблокируйте его';
export const DEFAULT_MODULE_CONFLICT_STOP_POLICY: Record<ModuleUxProfile, ConflictReason[]> = {
  client: ['collision', 'floor', 'bounds', 'scale', 'frozen-axis'],
  technologist: ['collision', 'floor', 'bounds', 'frozen-axis'],
  install: ['collision', 'floor'],
};

export function resolveModuleEditModeCapability(
  mode: ModuleEditMode,
  activeModuleId: string | null | undefined,
  lockedModuleIds: readonly string[],
): ModuleEditModeCapability {
  if (mode === 'off') return { enabled: true };
  if (!activeModuleId) return { enabled: false, reason: MODULE_EDIT_NO_ACTIVE_REASON };
  if (lockedModuleIds.includes(activeModuleId)) return { enabled: false, reason: MODULE_EDIT_LOCKED_REASON };
  return { enabled: true };
}

function safeViewerStorage(): Storage {
  try {
    if (typeof localStorage !== 'undefined' && typeof localStorage.setItem === 'function') {
      return localStorage;
    }
  } catch {
    // Fall through to in-memory storage for SSR, sandboxed iframes, and tests.
  }
  const mem = new Map<string, string>();
  return {
    getItem: (key) => mem.get(key) ?? null,
    setItem: (key, value) => { mem.set(key, String(value)); },
    removeItem: (key) => { mem.delete(key); },
    clear: () => { mem.clear(); },
    key: (index) => Array.from(mem.keys())[index] ?? null,
    get length() { return mem.size; },
  } as Storage;
}

type ViewerState = {
  // runtime — not persisted
  activeModelId: string | null;
  activeModelEpoch: number;
  selectedId: string | null;
  activeModuleId: string | null;
  viewerLayerMode: ViewerLayerMode;
  moduleEditMode: ModuleEditMode;
  moduleUxProfile: ModuleUxProfile;
  /** A16: пользовательский шаг сетки мм по профилю (null = дефолт снап-констант). */
  gridSnapOverrideMm: number | null;
  moduleConflictStopPolicy: Record<ModuleUxProfile, ConflictReason[]>;
  moduleTransforms: Record<string, ModuleTransform>;
  moduleNotes: Record<string, string>;
  moduleEditRevision: number;
  moduleSaveStatus: 'idle' | 'dirty' | 'saving' | 'saved' | 'failed' | 'conflict';
  setModuleSaveStatus: (status: ViewerState['moduleSaveStatus']) => void;
  moduleMovePreviewActive: boolean;
  // Active plan anchor point («точка привязки» à la Bazis): the corner/edge/
  // centre of the module footprint that numeric X/Z entry and drag place at a
  // coordinate. Sticky across selections (pro CAD convention).
  moduleAnchorPoint: AnchorPointId;
  panelGizmoMode: boolean;
  /** When true, the panel-level gizmo shows the rotate rings instead of the
   *  translate arrows.  Session-only (not persisted); default translate. */
  panelGizmoRotate: boolean;
  /** Module ids the user has locked/pinned.  Locked modules are excluded from
   *  neighbour magnetic snap and from the "resolve all" solver's pushes (they
   *  are fed to the solver as `fixed`).  Session-only, keyed by module id, so
   *  it is cleared on model switch to avoid cross-model id collisions. */
  lockedModuleIds: string[];
  /** Toggles wall_X / floor / ceiling primitives to semi-transparent (opacity ~0.3).
   *  Lets the user see furniture inside rooms without hiding the shell. */
  wallsTransparent: boolean;
  /** When true, walls between the camera and orbit target are auto-hidden
   *  on approach (см. useWallAutoHide). */
  wallsAutoHide: boolean;
  looseFurnitureMode: 'procedural' | 'glb';
  visualizationLayerVisible: boolean;
  panelTransforms: Record<string, PanelTransform>;
  // copy/paste buffer of module-instance ids (persisted so a copy survives a
  // model switch — that's what enables cross-model paste).
  moduleClipboard: ModuleClipboard | null;

  // user settings — persisted
  projection: Projection;
  renderMode: RenderMode;
  lightPreset: LightPreset;
  hdriPreset: HdriPreset;
  effects: EffectsCfg;
  exposure: number;
  hdriRotationDeg: number;
  hdriIntensity: number;
  hdriBackground: boolean;
  /** Display-only palette keyed by immutable source SHA, never by reusable
   * entity ids. It is not a material/catalog/BOM authority. */
  sourceScenePalettes: Record<string, SourceScenePalette>;
  explode: number;
  /* Эргономика (reach zones): переключатель слоя и рост человека, мм.
   * Оживляет донорскую фичу «дотянется/стремянка» в основной сцене. */
  ergonomicsActive: boolean;
  ergonomicsStatureMm: number;
  /* ВОЛНА W4 «режим стоимости»: модули красятся heat-градиентом по их строкам
   * в живой смете (легенда-шкала — в углу сцены). Режим просмотра, не данные:
   * не переживает перезагрузку, как линейка. */
  costHeatActive: boolean;
  sectionAxis: SectionAxis;
  sectionPosition: number;
  dimensionScope: DimensionScope;
  dimensionStyle: DimensionStyle;
  /* Ортокамера «Чертёж»: активный плоский вид или null (режим снят).
   * Вводится в обход projection только ЗАПОМИНАНИЕМ: projection остаётся
   * единственным владельцем «чем рисуем» (см. sceneCameras.ts). */
  draftingView: DraftingView | null;
  /**
   * Линейка включена: щелчок в сцене снимает замер, а не меняет выделение.
   * Это РЕЖИМ ИНСТРУМЕНТА, а не настройка вида, поэтому он НЕ переживает перезагрузку —
   * линейка, забытая включённой неделю назад, встретила бы человека молчащим выделением.
   */
  rulerActive: boolean;
  /**
   * ВОЛНА W2 «микро-звук»: mute синтезированных откликов сцены (магнит,
   * колёсико купе, доводчик). ПРЕДПОЧТЕНИЕ человека — переживает перезагрузку.
   * По умолчанию включён: звуки ≤0.15 гейна и стартуют только от жеста.
   */
  sceneAudioMuted: boolean;
  // per-element animation targets (panelId -> 0..1)
  openedPanels: Record<string, number>;
  // multi-selection: ids beyond the primary selectedId
  selectedIds: string[];
  // ids whose mesh.visible is forced off via context-menu / multi-select hide
  hiddenIds: string[];
  // 'part' = click picks individual panel; 'module' = walks up to enclosing module entity
  selectionMode: SelectionMode;

  setActiveModel: (id: string | null) => void;
  setSelected: (id: string | null) => void;
  toggleSelected: (id: string) => void;
  setSelectedMany: (ids: string[]) => void;
  clearSelection: () => void;
  hideEntities: (ids: string[]) => void;
  showEntities: (ids: string[]) => void;
  hideSelected: () => void;
  showAll: () => void;
  isolateSelected: () => void;
  setSelectionMode: (mode: SelectionMode) => void;
  setActiveModule: (id: string | null) => void;
  setViewerLayerMode: (mode: ViewerLayerMode) => void;
  setModuleEditMode: (mode: ModuleEditMode) => void;
  setModuleUxProfile: (profile: ModuleUxProfile) => void;
  setGridSnapOverrideMm: (mm: number | null) => void;
  setModuleConflictStopPolicy: (profile: ModuleUxProfile, reasons: ConflictReason[]) => void;
  resetModuleConflictStopPolicy: (profile?: ModuleUxProfile) => void;
  setModuleState: (state: {
    transforms?: Record<string, ModuleTransform>;
    notes?: Record<string, string>;
    activeModuleId?: string | null;
  }) => void;
  setModuleTransform: (
    moduleId: string,
    patch: Partial<ModuleTransform> & { position?: [number, number, number]; scale?: [number, number, number] },
  ) => void;
  commitModuleTransforms: (expected: Record<string, ModuleTransform>, next: Record<string, ModuleTransform>) => void;
  resetModuleTransform: (moduleId: string) => void;
  setModuleMovePreviewActive: (active: boolean) => void;
  setModuleAnchorPoint: (anchor: AnchorPointId) => void;
  cycleModuleAnchorPoint: (dir: 1 | -1) => void;
  setPanelGizmoMode: (on: boolean) => void;
  setPanelGizmoRotate: (on: boolean) => void;
  toggleModuleLock: (moduleId: string) => void;
  setWallsTransparent: (on: boolean) => void;
  setWallsAutoHide: (on: boolean) => void;
  setLooseFurnitureMode: (mode: 'procedural' | 'glb') => void;
  setVisualizationLayerVisible: (visible: boolean) => void;
  setPanelTransform: (entityId: string, patch: PanelTransform) => void;
  resetPanelTransform: (entityId: string) => void;
  clearPanelTransforms: () => void;
  copyModules: (modelId: string | null | undefined, ids: string[]) => void;
  clearModuleClipboard: () => void;
  setModuleNote: (moduleId: string, note: string) => void;
  clearModuleNote: (moduleId: string) => void;
  setProjection: (p: Projection) => void;
  setRenderMode: (m: RenderMode) => void;
  setLightPreset: (l: LightPreset) => void;
  setHdriPreset: (h: HdriPreset) => void;
  setEffects: (e: Partial<EffectsCfg>) => void;
  setExposure: (v: number) => void;
  setHdriRotation: (v: number) => void;
  setHdriIntensity: (v: number) => void;
  setHdriBackground: (v: boolean) => void;
  setSourceScenePalette: (sourceSha256: string, palette: SourceScenePalette) => void;
  setExplode: (v: number) => void;
  setErgonomicsActive: (v: boolean) => void;
  setErgonomicsStatureMm: (v: number) => void;
  setCostHeatActive: (v: boolean) => void;
  setSection: (axis: SectionAxis, position?: number) => void;
  setSectionPosition: (p: number) => void;
  setDimensionScope: (s: DimensionScope) => void;
  setDimensionStyle: (s: DimensionStyle) => void;
  /** Ортокамера «Чертёж»: вид (и сразу проекция под него) или null — снять.
   * Ручная смена projection на другой вид снимает режим сам (setProjection). */
  setDraftingView: (view: DraftingView | null) => void;
  setRulerActive: (v: boolean) => void;
  setSceneAudioMuted: (v: boolean) => void;
  togglePanelOpen: (id: string) => void;
  setPanelOpen: (id: string, t: number) => void;
  openAllPanels: () => void;
  closeAllPanels: () => void;
};

export const useViewerStore = create<ViewerState>()(
  persist(
    (set) => ({
      activeModelId: null,
      activeModelEpoch: 0,
      selectedId: null,
      selectedIds: [],
      hiddenIds: [],
      selectionMode: 'module',
      activeModuleId: null,
      viewerLayerMode: 'body',
      moduleEditMode: 'off',
      moduleUxProfile: 'technologist',
      gridSnapOverrideMm: null,
      moduleConflictStopPolicy: cloneStopPolicy(DEFAULT_MODULE_CONFLICT_STOP_POLICY),
      moduleTransforms: {},
      moduleNotes: {},
      moduleMovePreviewActive: false,
      moduleAnchorPoint: DEFAULT_ANCHOR_POINT,
      panelGizmoMode: false,
      panelGizmoRotate: false,
      lockedModuleIds: [],
      /* СТЕНЫ РИСУЮТСЯ ТЕЛОМ, А БЛИЖНЯЯ УБИРАЕТСЯ САМА.
         Прозрачные стены были компромиссом при выключенном автопрятании: канал не видел ни
         одной стены вьюера (искал чужой признак), а старое правило подбирало пороги и в
         плотной квартире прятало лишнее. Комната из-за этого читалась стеклянным ящиком.
         Правило заменено точным — прячется ровно та стена, за которой стоит камера, — и стены
         могут быть тем, чем они являются. Оба переключателя остались. */
      wallsTransparent: false,
      /* Ближняя стена прячется сама: без этого комната открывается «изнутри стены» и человек
         крутит камеру, чтобы найти угол, откуда видно. Прежде канал был выключен, потому что
         старое правило подбирало пороги и в плотной квартире прятало лишнее; правило заменено
         точным (`ApartmentCutaway.ts`), и прятать стало нечего бояться. */
      wallsAutoHide: true,
      looseFurnitureMode: 'procedural',
      visualizationLayerVisible: true,  // GLB с реальными мешами — включается в настройках
      panelTransforms: {},
      moduleClipboard: null,
      moduleEditRevision: 0,
      moduleSaveStatus: 'idle',
      setModuleSaveStatus: (moduleSaveStatus) => set({ moduleSaveStatus }),
      projection: 'perspective',
      renderMode: 'solid',
      lightPreset: 'showroom',
      hdriPreset: 'neutral',  // 30.09: тёплое небо умывало сцену в оранжевый кисель
      effects: { ssao: true, bloom: true, photoreal: true, photoDesaturation: 0, photoDepthOfField: 0, catalogColors: false, selectiveEmissiveBloom: true },  // 30.09: премиум-дефолт — SSAO+bloom ON
      exposure: 1,
      hdriRotationDeg: 0,
      hdriIntensity: 1,
      hdriBackground: true,  // 30.09: премиум-дефолт — сцена в небе, а не в сером вакууме студии
      sourceScenePalettes: {},
      explode: 0,
      ergonomicsActive: false,
      ergonomicsStatureMm: 1700,
      costHeatActive: false,
      sectionAxis: null,
      sectionPosition: 0.5,
      dimensionScope: 'off',
      dimensionStyle: 'production',
      draftingView: null,
      rulerActive: false,
      sceneAudioMuted: false,
      openedPanels: {},
      setActiveModel: (activeModelId) => {
        // The URL is the reload/share authority used by App. Keep it in the
        // same owner as the active id so every selector (top bar, flyout,
        // create flow and recovery link) has identical navigation semantics.
        if (typeof window !== 'undefined') {
          const target = new URL(window.location.href);
          const currentModel = target.searchParams.get('model') || target.searchParams.get('m');
          if (activeModelId) target.searchParams.set('model', activeModelId);
          else target.searchParams.delete('model');
          target.searchParams.delete('m');
          if (!activeModelId || currentModel !== activeModelId) target.searchParams.delete('publishedView');
          window.history.replaceState(window.history.state, '', target);
        }
        set((state) => ({
          activeModelId,
          activeModelEpoch: state.activeModelEpoch + 1,
          selectedId: null,
          selectedIds: [],
          hiddenIds: [],
          activeModuleId: null,
          viewerLayerMode: 'body',
          renderMode: 'solid',
          effects: { ssao: true, bloom: true, photoreal: false, photoDesaturation: 0, photoDepthOfField: 0, catalogColors: false, selectiveEmissiveBloom: true },  // 30.09: премиум-дефолт — SSAO+bloom ON
          exposure: 1,
          hdriBackground: false,
          openedPanels: {},
          explode: 0,
      ergonomicsActive: false,
      ergonomicsStatureMm: 1700,
      costHeatActive: false,
          sectionAxis: null,
          sectionPosition: 0.5,
          moduleTransforms: {},
          moduleNotes: {},
          moduleEditRevision: 0,
          moduleSaveStatus: 'idle',
          moduleMovePreviewActive: false,
          // Locks are keyed by module id which may collide across models —
          // clear on switch so a lock never leaks onto a different model.
          lockedModuleIds: [],
          // Clear panel gizmo offsets — they're keyed by entity id which
          // may collide across models, causing geometry to "explode".
          panelTransforms: {},
        }));
      },
      setSelected: (selectedId) =>
        set({ selectedId, selectedIds: selectedId ? [selectedId] : [] }),
      toggleSelected: (id) =>
        set((s) => {
          const has = s.selectedIds.includes(id);
          const nextIds = has ? s.selectedIds.filter((x) => x !== id) : [...s.selectedIds, id];
          return {
            selectedIds: nextIds,
            selectedId: nextIds.length === 1 ? nextIds[0] : has ? nextIds[nextIds.length - 1] ?? null : id,
          };
        }),
      setSelectedMany: (ids) =>
        set({ selectedIds: [...new Set(ids)], selectedId: ids.length ? ids[ids.length - 1] : null }),
      clearSelection: () => set({ selectedId: null, selectedIds: [] }),
      hideEntities: (ids) =>
        set((s) => ({ hiddenIds: [...new Set([...s.hiddenIds, ...ids])] })),
      showEntities: (ids) =>
        set((s) => {
          const drop = new Set(ids);
          return { hiddenIds: s.hiddenIds.filter((x) => !drop.has(x)) };
        }),
      hideSelected: () =>
        set((s) => ({ hiddenIds: [...new Set([...s.hiddenIds, ...s.selectedIds])] })),
      showAll: () => set({ hiddenIds: [] }),
      setSelectionMode: (selectionMode) => set({ selectionMode }),
      isolateSelected: () =>
        set((s) => {
          if (!s.selectedIds.length) return {};
          // hidden = all entities except selected — caller will fill from bundle
          // we store only selected list here; useRenderModes consumes hiddenIds
          // To "isolate" we set hiddenIds via a marker. The actual computation
          // happens in the scene layer (it knows all entityIds).
          return { hiddenIds: [`__isolate:${s.selectedIds.join(',')}`] };
        }),
      setActiveModule: (activeModuleId) =>
        set((s) => ({
          activeModuleId,
          ...(!activeModuleId || s.lockedModuleIds.includes(activeModuleId) ? { moduleEditMode: 'off' } : {}),
        })),
      setViewerLayerMode: (viewerLayerMode) =>
        set({
          viewerLayerMode,
          explode: 0,
      ergonomicsActive: false,
      ergonomicsStatureMm: 1700,
      costHeatActive: false,
          sectionAxis: null,
          ...(viewerLayerMode === 'body' ? { openedPanels: {} } : {}),
        }),
      setModuleEditMode: (moduleEditMode) =>
        set((s) => ({
          moduleEditMode: resolveModuleEditModeCapability(
            moduleEditMode,
            s.activeModuleId,
            s.lockedModuleIds,
          ).enabled
            ? moduleEditMode
            : 'off',
        })),
      setModuleUxProfile: (moduleUxProfile) => set({ moduleUxProfile }),
      setGridSnapOverrideMm: (gridSnapOverrideMm) => set({ gridSnapOverrideMm }),
      setModuleConflictStopPolicy: (profile, reasons) =>
        set((s) => ({
          moduleConflictStopPolicy: {
            ...s.moduleConflictStopPolicy,
            [profile]: normalizeReasons(reasons),
          },
        })),
      resetModuleConflictStopPolicy: (profile) =>
        set((s) => {
          if (!profile) {
            return { moduleConflictStopPolicy: cloneStopPolicy(DEFAULT_MODULE_CONFLICT_STOP_POLICY) };
          }
          return {
            moduleConflictStopPolicy: {
              ...s.moduleConflictStopPolicy,
              [profile]: [...DEFAULT_MODULE_CONFLICT_STOP_POLICY[profile]],
            },
          };
        }),
      setModuleState: ({ transforms, notes, activeModuleId }) =>
        set((s) => ({
          moduleTransforms: transforms ? normalizeModuleTransforms(transforms) : s.moduleTransforms,
          moduleNotes: notes ?? s.moduleNotes,
          activeModuleId: activeModuleId !== undefined ? activeModuleId : s.activeModuleId,
        })),
      setModuleTransform: (moduleId, patch) =>
        set((s) => {
          // VF-FE-12: a locked module is locked for every writer, including
          // the silent store path the keyboard hints use.
          if (s.lockedModuleIds.includes(moduleId)) return {};
          const prev = s.moduleTransforms[moduleId] ?? {
            position: [0, 0, 0] as [number, number, number],
            scale: [1, 1, 1] as [number, number, number],
          };
          return {
            moduleTransforms: {
              ...s.moduleTransforms,
              [moduleId]: {
                position: patch.position ?? prev.position,
                scale: [1, 1, 1],
              },
            },
            moduleEditRevision: s.moduleEditRevision + 1, moduleSaveStatus: 'dirty',
          };
        }),
      commitModuleTransforms: (expected, next) => set((s) => {
        if (!moduleTransformsApproximatelyEqual(s.moduleTransforms, expected)) throw new Error('MOVE_PREVIEW_STALE');
        for (const id of new Set([...Object.keys(expected), ...Object.keys(next)])) {
          const before = expected[id]?.position ?? [0, 0, 0];
          const after = next[id]?.position ?? [0, 0, 0];
          if (after.length !== 3 || !after.every(Number.isFinite)) throw new Error('MOVE_POSITION_INVALID');
          if (s.lockedModuleIds.includes(id) && before.some((v, axis) => v !== after[axis])) throw new Error('MOVE_LOCKED_CHANGED');
        }
        return { moduleTransforms: normalizeModuleTransforms(next), moduleEditRevision: s.moduleEditRevision + 1, moduleSaveStatus: 'dirty' };
      }),
      resetModuleTransform: (moduleId) =>
        set((s) => {
          const next = { ...s.moduleTransforms };
          delete next[moduleId];
          return { moduleTransforms: next, moduleEditRevision: s.moduleEditRevision + 1, moduleSaveStatus: 'dirty' };
        }),
      setModuleMovePreviewActive: (moduleMovePreviewActive) => set({ moduleMovePreviewActive }),
      setModuleAnchorPoint: (moduleAnchorPoint) => set({ moduleAnchorPoint }),
      cycleModuleAnchorPoint: (dir) =>
        set((s) => ({
          moduleAnchorPoint:
            dir === 1 ? nextAnchorPoint(s.moduleAnchorPoint) : prevAnchorPoint(s.moduleAnchorPoint),
        })),
      setPanelGizmoMode: (panelGizmoMode) => set({ panelGizmoMode }),
      setPanelGizmoRotate: (panelGizmoRotate) => set({ panelGizmoRotate }),
      toggleModuleLock: (moduleId) =>
        set((s) => {
          const has = s.lockedModuleIds.includes(moduleId);
          return {
            lockedModuleIds: has
              ? s.lockedModuleIds.filter((id) => id !== moduleId)
              : [...s.lockedModuleIds, moduleId],
            ...(has || s.activeModuleId !== moduleId ? {} : { moduleEditMode: 'off' }),
          };
        }),
      setWallsTransparent: (wallsTransparent) => set({ wallsTransparent }),
      setWallsAutoHide: (wallsAutoHide) => set({ wallsAutoHide }),
      setLooseFurnitureMode: (looseFurnitureMode: 'procedural' | 'glb') => set({ looseFurnitureMode }),
      setVisualizationLayerVisible: (visualizationLayerVisible: boolean) => set({ visualizationLayerVisible }),
      setPanelTransform: (entityId, patch) =>
        set((s) => ({
          panelTransforms: { ...s.panelTransforms, [entityId]: patch },
        })),
      resetPanelTransform: (entityId) =>
        set((s) => {
          const next = { ...s.panelTransforms };
          delete next[entityId];
          return { panelTransforms: next };
        }),
      clearPanelTransforms: () => set({ panelTransforms: {} }),
      copyModules: (modelId, ids) =>
        set(() => {
          const moduleIds = [...new Set(ids.filter((id) => typeof id === 'string' && id.trim().length > 0))];
          if (!modelId || moduleIds.length === 0) return { moduleClipboard: null };
          return { moduleClipboard: { sourceModelId: modelId, moduleIds } };
        }),
      clearModuleClipboard: () => set({ moduleClipboard: null }),
      setModuleNote: (moduleId, note) =>
        set((s) => ({
          moduleNotes: {
            ...s.moduleNotes,
            [moduleId]: note,
          },
          moduleEditRevision: s.moduleEditRevision + 1, moduleSaveStatus: 'dirty',
        })),
      clearModuleNote: (moduleId) =>
        set((s) => {
          const next = { ...s.moduleNotes };
          delete next[moduleId];
          return { moduleNotes: next, moduleEditRevision: s.moduleEditRevision + 1, moduleSaveStatus: 'dirty' };
        }),
      /* Ручная проекция — хозяин камеры: выбор вида, не совпадающего с активным
         «Чертёжём», снимает режим (чертёж не должен спорить с человеком за
         камеру). Совпадающая смена — прозрачна (например, повторный «Спереди»). */
      setProjection: (projection) => set((s) => ({ projection, draftingView: s.draftingView && s.draftingView !== projection ? null : s.draftingView })),
      setRenderMode: (renderMode) => set({ renderMode }),
      setLightPreset: (lightPreset) => set({ lightPreset }),
      setHdriPreset: (hdriPreset) => set({ hdriPreset }),
      setEffects: (patch) => set((s) => ({ effects: { ...s.effects, ...patch } })),
      setExposure: (exposure) => set({ exposure }),
      setHdriRotation: (hdriRotationDeg) => set({ hdriRotationDeg }),
      setHdriIntensity: (hdriIntensity) => set({ hdriIntensity }),
      setHdriBackground: (hdriBackground) => set({ hdriBackground }),
      setSourceScenePalette: (sourceSha256, palette) => set((state) => {
        if (!/^[a-f0-9]{64}$/i.test(sourceSha256)) throw new Error('VIEWER_SOURCE_SCENE_PALETTE_SOURCE_INVALID');
        if (!['halifax', 'casella_light'].includes(palette.facadeDecor)) throw new Error('VIEWER_SOURCE_SCENE_PALETTE_DECOR_INVALID');
        if (!/^(?:#[a-f0-9]{6})$/i.test(palette.carcassColorHex) || !/^(?:#[a-f0-9]{6})$/i.test(palette.backColorHex)) {
          throw new Error('VIEWER_SOURCE_SCENE_PALETTE_COLOR_INVALID');
        }
        return { sourceScenePalettes: { ...state.sourceScenePalettes, [sourceSha256]: { ...palette } } };
      }),
      setExplode: (explode) => set({ explode }),
      setErgonomicsActive: (ergonomicsActive) => set({ ergonomicsActive }),
      setErgonomicsStatureMm: (ergonomicsStatureMm) => set({ ergonomicsStatureMm }),
      setCostHeatActive: (costHeatActive) => set({ costHeatActive }),
      setSection: (sectionAxis, sectionPosition) =>
        set((s) => ({
          sectionAxis,
          sectionPosition: sectionPosition ?? s.sectionPosition,
        })),
      setSectionPosition: (sectionPosition) => set({ sectionPosition }),
      setDimensionScope: (dimensionScope) => set({ dimensionScope }),
      /* «Чертёж»: вид задаю вместе с проекцией — один владелец кадра. Снятие
         (null) проекцию НЕ трогает: человек остаётся в плоском виде, в котором
         был, и уходит из него руками. */
      setDraftingView: (draftingView) =>
        set((s) => (draftingView ? { draftingView, projection: draftingView } : { draftingView })),
      setDimensionStyle: (dimensionStyle) => set({ dimensionStyle }),
      setRulerActive: (rulerActive) => set({ rulerActive }),
      setSceneAudioMuted: (sceneAudioMuted) => set({ sceneAudioMuted }),
      togglePanelOpen: (id) =>
        set((s) => {
          const cur = s.openedPanels[id] ?? 0;
          const next = cur > 0.5 ? 0 : 1;
          const updated = { ...s.openedPanels, [id]: next };
          if (next === 0) delete updated[id];
          return { openedPanels: updated };
        }),
      setPanelOpen: (id, t) =>
        set((s) => {
          const updated = { ...s.openedPanels };
          if (t <= 0.001) delete updated[id];
          else updated[id] = Math.min(1, Math.max(0, t));
          return { openedPanels: updated };
        }),
      openAllPanels: () => set({ openedPanels: { __all: 1 } }),
      closeAllPanels: () => set({ openedPanels: {} }),
    }),
    {
      name: 'holzcom-viewer-v2',
      version: 17,
      storage: createJSONStorage(safeViewerStorage),
      // Непрочитанное сохранённое состояние (порченый localStorage, упавшая migrate()) НЕ
      // проглатывается тихо в дефолтный вид — см. unreadableSavedState.ts: zustand persist
      // по умолчанию ловит эту ошибку и ничего с ней не делает, если этот колбэк не задан.
      onRehydrateStorage: () => (_state, error) => {
        if (error) reportUnreadableSavedState('просмотрщик', error);
      },
      migrate: (persisted: unknown, fromVersion: number) => {
        let base = (persisted && typeof persisted === 'object' ? persisted : {}) as Partial<ViewerState>;
        if (fromVersion < 4) {
          base = {
            ...base,
            lightPreset: 'showroom',
            exposure: 1,
            hdriBackground: false,
            effects: {
              ssao: true,
              bloom: true,
              photoDesaturation: 0,
              photoDepthOfField: 0,
              catalogColors: false,
              photoreal: false,
              selectiveEmissiveBloom: true,
              ...(base.effects ?? {}),
            },
            moduleEditMode: 'off',
            moduleTransforms: {},
            moduleNotes: {},
            activeModuleId: null,
            moduleUxProfile: 'technologist',
            moduleConflictStopPolicy: cloneStopPolicy(DEFAULT_MODULE_CONFLICT_STOP_POLICY),
          };
        }
        if (fromVersion < 5) {
          base = {
            ...base,
            moduleUxProfile: base.moduleUxProfile ?? 'technologist',
            moduleConflictStopPolicy: cloneStopPolicy(DEFAULT_MODULE_CONFLICT_STOP_POLICY),
          };
        }
        if (fromVersion < 6) {
          const rawPolicy =
            base.moduleConflictStopPolicy && typeof base.moduleConflictStopPolicy === 'object'
              ? base.moduleConflictStopPolicy
              : undefined;
          base = {
            ...base,
            moduleConflictStopPolicy: mergeStopPolicy(rawPolicy),
          };
        }
        if (fromVersion < 7) {
          // v7: persist panel-gizmo flag + the offsets it produces, so the
          // user doesn't lose a panel-level edit on accidental refresh.
          const merged = { ...base } as Partial<ViewerState> & Record<string, unknown>;
          if (typeof merged.panelGizmoMode !== 'boolean') merged.panelGizmoMode = false;
          if (!merged.panelTransforms || typeof merged.panelTransforms !== 'object') merged.panelTransforms = {};
          base = merged as Partial<ViewerState>;
        }
        if (fromVersion < 8) {
          // v8: panelTransforms removed from persistence (entity-id keys
          // collide across models → "exploding scene" bug).  Nuke any
          // stale offsets stored from old versions so the user's scene
          // doesn't keep blowing up after they upgrade.
          const merged = { ...base } as Partial<ViewerState> & Record<string, unknown>;
          delete merged.panelTransforms;
          base = merged as Partial<ViewerState>;
        }
        if (fromVersion < 9) {
          // v9: usePanelGizmo was applying panelTransforms as ABSOLUTE
          // positions (not additive offsets), which caused dramatic visual
          // explosion across model loads.  Re-nuke any panelTransforms that
          // somehow leaked back into storage despite v8's partialize drop —
          // belt-and-suspenders since the explosion bug just shipped.
          const merged = { ...base } as Partial<ViewerState> & Record<string, unknown>;
          delete merged.panelTransforms;
          delete merged.moduleTransforms;       // same risk for module-level
          delete merged.panelGizmoMode;         // start in safe (off) state
          base = merged as Partial<ViewerState>;
        }
        if (fromVersion < 10) {
          // v10: canonical/generated scenes use stable presentation materials.
          // Reset persisted destructive/photographic render state so a reload
          // cannot inherit X-Ray translucency or bloom glare from an old build.
          base = {
            ...base,
            renderMode: 'solid',
            effects: {
              ...(base.effects ?? {}),
              ssao: true,
              photoDesaturation: base.effects?.photoDesaturation ?? 0,
              photoDepthOfField: base.effects?.photoDepthOfField ?? 0,
              bloom: true,
              photoreal: false,
              catalogColors: false,
              selectiveEmissiveBloom: true,
            },
            exposure: 1,
            hdriBackground: false,
          };
        }
        if (fromVersion < 12) {
          base = {
            ...base,
            selectedIds: [],
            renderMode: 'solid',
            viewerLayerMode: 'body',
            openedPanels: {},
            effects: {
              ...(base.effects ?? {}),
              ssao: true,
              photoDesaturation: base.effects?.photoDesaturation ?? 0,
              photoDepthOfField: base.effects?.photoDepthOfField ?? 0,
              bloom: true,
              photoreal: false,
              catalogColors: false,
              selectiveEmissiveBloom: true,
            },
            exposure: 1,
            hdriBackground: false,
          };
        }
        if (fromVersion < 17) {
          base = {
            ...base,
            hdriIntensity: typeof base.hdriIntensity === 'number' ? base.hdriIntensity : 1,
            effects: {
              ssao: true,
              bloom: true,
              photoreal: false,
              photoDesaturation: 0,
              photoDepthOfField: 0,
              catalogColors: false,
              selectiveEmissiveBloom: true,
              ...(base.effects ?? {}),
            },
          };
        }
        if (fromVersion < 13) {
          base = {
            ...base,
            moduleAnchorPoint: isAnchorPointId(base.moduleAnchorPoint)
              ? base.moduleAnchorPoint
              : DEFAULT_ANCHOR_POINT,
          };
        }
        if (fromVersion < 14) {
          // Anchor preference survives reload; edit selection does not. The
          // target must be explicitly resolved/reselected against the freshly
          // loaded canonical graph before controls become active.
          const { selectedId: _selectedId, selectedIds: _selectedIds, ...safe } = base;
          base = safe;
        }
        if (fromVersion < 15) {
          // v15: стены не должны загораживать комнату на первом открытии.
          // 03.10: false выключал авто-скрытие НАВСегДА для старых сессий —
          // противоречит самому комментарию; комнаты были заперты стенами.
          base = {
            ...base,
            wallsTransparent: true,
            wallsAutoHide: true,
          };
        }
        if (fromVersion < 16) {
          // v16 объявили две ветки сразу, и обе правки нужны сохранённой сессии.
          //
          // Палитры сцены заводятся пустыми: прежде их в сохранении не было вовсе.
          //
          // Комната перестала быть стеклянным ящиком. Прозрачность была ответом на
          // выключенное автопрятание; теперь прячется ровно та стена, за которой стоит
          // камера, и стены рисуются телом. Прежний сохранённый выбор не должен вернуть
          // человека к стеклу — иначе он увидит старое поведение и решит, что ничего не
          // изменилось.
          base = {
            ...base,
            sourceScenePalettes: {},
            wallsTransparent: false,
            wallsAutoHide: true,
          };
        }
        return base;
      },
      partialize: (s) => ({
        activeModelId: s.activeModelId,  // remember last opened model across refresh
        projection: s.projection,
        lightPreset: s.lightPreset,
        hdriPreset: s.hdriPreset,
        hdriRotationDeg: s.hdriRotationDeg,
        hdriIntensity: s.hdriIntensity,
        hdriBackground: s.hdriBackground,
        exposure: s.exposure,
        effects: s.effects,
        sourceScenePalettes: s.sourceScenePalettes,
        dimensionScope: s.dimensionScope,
        dimensionStyle: s.dimensionStyle,
        // W2 микро-звук: preference — переживает перезагрузку.
        sceneAudioMuted: s.sceneAudioMuted,
        moduleUxProfile: s.moduleUxProfile,
        moduleConflictStopPolicy: s.moduleConflictStopPolicy,
        moduleAnchorPoint: s.moduleAnchorPoint,
        // selectionMode intentionally NOT persisted — default to 'module' each
        // load so a click selects the whole cabinet (part mode is a HUD toggle).
        viewerLayerMode: s.viewerLayerMode,
        panelGizmoMode: s.panelGizmoMode,
        // module copy buffer — persisted so a copy survives a refresh / model
        // switch, which is precisely what cross-model paste relies on.
        moduleClipboard: s.moduleClipboard,
        // panelTransforms intentionally NOT persisted — they're keyed by
        // entity id, and identical ids across DIFFERENT models cause the
        // saved offsets to "explode" the new model's geometry on load.
        // Session-scoped only.
        // note: sectionAxis/explode also not persisted — destructive view modes
      }),
    },
  ),
);

function normalizeModuleTransforms(transforms: Record<string, ModuleTransform>): Record<string, ModuleTransform> {
  const out: Record<string, ModuleTransform> = {};
  for (const [moduleId, transform] of Object.entries(transforms)) {
    out[moduleId] = {
      position: transform.position,
      scale: [1, 1, 1],
    };
  }
  return out;
}

const MODULE_TRANSFORM_EPS_MM = 0.001;

function moduleTransformsApproximatelyEqual(
  left: Record<string, ModuleTransform>,
  right: Record<string, ModuleTransform>,
): boolean {
  const leftKeys = Object.keys(left).sort();
  const rightKeys = Object.keys(right).sort();
  if (leftKeys.length !== rightKeys.length) return false;
  for (let i = 0; i < leftKeys.length; i += 1) {
    const key = leftKeys[i]!;
    if (key !== rightKeys[i]) return false;
    const a = left[key];
    const b = right[key];
    if (!a || !b) return false;
    for (let axis = 0; axis < 3; axis += 1) {
      if (Math.abs(a.position[axis]! - b.position[axis]!) > MODULE_TRANSFORM_EPS_MM) return false;
      if (Math.abs(a.scale[axis]! - b.scale[axis]!) > MODULE_TRANSFORM_EPS_MM) return false;
    }
  }
  return true;
}

function normalizeReasons(reasons: string[]): ConflictReason[] {
  const allowed: ConflictReason[] = ['collision', 'floor', 'bounds', 'scale', 'frozen-axis'];
  const next: ConflictReason[] = [];
  for (const reason of reasons) {
    if (allowed.includes(reason as ConflictReason) && !next.includes(reason as ConflictReason)) {
      next.push(reason as ConflictReason);
    }
  }
  return next;
}

function cloneStopPolicy(
  policy: Record<ModuleUxProfile, ConflictReason[]>,
): Record<ModuleUxProfile, ConflictReason[]> {
  return {
    client: [...policy.client],
    technologist: [...policy.technologist],
    install: [...policy.install],
  };
}

function mergeStopPolicy(
  raw: unknown,
): Record<ModuleUxProfile, ConflictReason[]> {
  const base = cloneStopPolicy(DEFAULT_MODULE_CONFLICT_STOP_POLICY);
  if (!raw || typeof raw !== 'object') return base;
  const source = raw as Partial<Record<ModuleUxProfile, unknown>>;
  return {
    client: normalizeReasons(Array.isArray(source.client) ? (source.client as string[]) : base.client),
    technologist: normalizeReasons(
      Array.isArray(source.technologist) ? (source.technologist as string[]) : base.technologist,
    ),
    install: normalizeReasons(Array.isArray(source.install) ? (source.install as string[]) : base.install),
  };
}
